import { useState } from 'react';
import { AnimatedNumber } from '../../components/AnimatedNumber/index';

interface InsightData {
  avgApprovalTimes: {
    AE: number;
    DEAL_DESK: number;
    CRO: number;
    LEGAL: number;
    FINANCE: number;
    CUSTOMER: number;
  };
  quotesByStage: {
    AE: number;
    DEAL_DESK: number;
    CRO: number;
    LEGAL: number;
    FINANCE: number;
    CUSTOMER: number;
  };
  approvalStats: {
    approved: number;
    rejected: number;
    pending: number;
    total: number;
  };
}

export const Insights = () => {
  const [insightData, setInsightData] = useState<InsightData | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);

  const generateMockInsights = () => {
    setIsGenerating(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const mockData: InsightData = {
        avgApprovalTimes: {
          AE: Math.round(Math.random() * 2 + 0.5), // 0.5-2.5 days
          DEAL_DESK: Math.round(Math.random() * 3 + 1), // 1-4 days
          CRO: Math.round(Math.random() * 5 + 2), // 2-7 days
          LEGAL: Math.round(Math.random() * 7 + 3), // 3-10 days
          FINANCE: Math.round(Math.random() * 4 + 2), // 2-6 days
          CUSTOMER: Math.round(Math.random() * 10 + 5), // 5-15 days
        },
        quotesByStage: {
          AE: Math.round(Math.random() * 20 + 5), // 5-25 quotes
          DEAL_DESK: Math.round(Math.random() * 15 + 3), // 3-18 quotes
          CRO: Math.round(Math.random() * 10 + 2), // 2-12 quotes
          LEGAL: Math.round(Math.random() * 8 + 1), // 1-9 quotes
          FINANCE: Math.round(Math.random() * 5 + 1), // 1-6 quotes
          CUSTOMER: Math.round(Math.random() * 12 + 2), // 2-14 quotes
        },
        approvalStats: {
          approved: Math.round(Math.random() * 30 + 20), // 20-50 approved
          rejected: Math.round(Math.random() * 10 + 2), // 2-12 rejected
          pending: Math.round(Math.random() * 15 + 5), // 5-20 pending
          total: 0 // Will be calculated
        }
      };

      // Calculate total
      mockData.approvalStats.total = 
        mockData.approvalStats.approved + 
        mockData.approvalStats.rejected + 
        mockData.approvalStats.pending;

      setInsightData(mockData);
      setIsGenerating(false);
    }, 1500);
  };

  const getApprovalPercentage = (approved: number, total: number) => {
    return total > 0 ? Math.round((approved / total) * 100) : 0;
  };

  const getRejectionPercentage = (rejected: number, total: number) => {
    return total > 0 ? Math.round((rejected / total) * 100) : 0;
  };

  return (
    <div className="page">
      <div className="page-header">
        <h1>📊 Insights</h1>
        <button 
          onClick={generateMockInsights}
          className="generate-insights-button"
          disabled={isGenerating}
        >
          {isGenerating ? '⏳ Generating...' : '🎲 Generate Insights on Mock Data'}
        </button>
      </div>

      {!insightData ? (
        <div className="insights-empty">
          <div className="empty-state-content">
            <div className="empty-state-icon">📈</div>
            <h2>Generate CPQ Analytics</h2>
            <p>Click the button above to generate sample insights and analytics from mock quote data.</p>
            <div className="upcoming-features">
              <h3>Analytics Include:</h3>
              <ul>
                <li>📊 Average approval time per persona</li>
                <li>🎯 Quotes by current stage breakdown</li>
                <li>✅ Approval vs rejection percentages</li>
              </ul>
            </div>
          </div>
        </div>
      ) : (
        <div className="insights-dashboard">
          {/* Average Approval Times */}
          <div className="card">
            <h3>⏱️ Average Approval Time per Persona</h3>
            <div className="approval-times-grid">
              {Object.entries(insightData.avgApprovalTimes).map(([persona, days]) => (
                <div key={persona} className="approval-time-item">
                  <div className="persona-label">{persona.replace('_', ' ')}</div>
                  <div className="approval-time">
                    <AnimatedNumber value={days} suffix=" days" duration={1000} />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Quotes by Stage */}
          <div className="card">
            <h3>🎯 Quotes by Current Stage</h3>
            <div className="quotes-by-stage-grid">
              {Object.entries(insightData.quotesByStage).map(([persona, count]) => (
                <div key={persona} className="stage-quote-item">
                  <div className="stage-info">
                    <div className="stage-persona">{persona.replace('_', ' ')}</div>
                    <div className="stage-count">
                      <AnimatedNumber value={count} suffix=" quotes" duration={1200} />
                    </div>
                  </div>
                  <div className="stage-bar">
                    <div 
                      className="stage-fill"
                      style={{ 
                        width: `${(count / Math.max(...Object.values(insightData.quotesByStage))) * 100}%` 
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Approval Statistics */}
          <div className="card">
            <h3>✅ Approval Statistics</h3>
            <div className="approval-stats-grid">
              <div className="approval-stat approved">
                <div className="stat-icon">✅</div>
                <div className="stat-info">
                  <div className="stat-number">
                    <AnimatedNumber value={getApprovalPercentage(insightData.approvalStats.approved, insightData.approvalStats.total)} suffix="%" duration={1500} />
                  </div>
                  <div className="stat-label">Approved</div>
                  <div className="stat-count">
                    <AnimatedNumber value={insightData.approvalStats.approved} suffix=" quotes" duration={1300} />
                  </div>
                </div>
              </div>
              
              <div className="approval-stat rejected">
                <div className="stat-icon">❌</div>
                <div className="stat-info">
                  <div className="stat-number">
                    <AnimatedNumber value={getRejectionPercentage(insightData.approvalStats.rejected, insightData.approvalStats.total)} suffix="%" duration={1500} />
                  </div>
                  <div className="stat-label">Rejected</div>
                  <div className="stat-count">
                    <AnimatedNumber value={insightData.approvalStats.rejected} suffix=" quotes" duration={1300} />
                  </div>
                </div>
              </div>

              <div className="approval-stat pending">
                <div className="stat-icon">📋</div>
                <div className="stat-info">
                  <div className="stat-number">
                    <AnimatedNumber value={Math.round((insightData.approvalStats.pending / insightData.approvalStats.total) * 100)} suffix="%" duration={1500} />
                  </div>
                  <div className="stat-label">Pending</div>
                  <div className="stat-count">
                    <AnimatedNumber value={insightData.approvalStats.pending} suffix=" quotes" duration={1300} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};